/**
 * Generic mailbox username suggestions for the success-screen nudge.
 *
 * Public surface is unchanged: `getSuggestions({ locale, existingUsernames,
 * count })` returns ordered, popular-tagged suggestions. The data lives in
 * `@/const/mailbox-suggestions.constant`.
 *
 * Locale lookup trims/lowercases the input, then matches by exact key (no
 * primary-subtag prefixing). Unknown locales fall back to DEFAULT_LOCALE_KEY.
 */

import {
  DEFAULT_LOCALE_KEY,
  MAILBOX_SUGGESTION_IDS,
  MAILBOX_SUGGESTION_POPULAR_COUNT,
  MAILBOX_SUGGESTION_VALUES,
} from "@/const/mailbox-suggestions.constant";

export type TMailboxSuggestion = {
  username: string;
  popular: boolean;
};

export type TGetSuggestionsOptions = {
  /** Locale string as reported by the Control Panel store (e.g. "en", "pt-br"). */
  locale: string | undefined | null;
  /** Usernames already created on the order — they're skipped. */
  existingUsernames?: string[];
  /** How many suggestions to return (3 if pending banner visible, else 5). */
  count?: number;
};

const resolveLocaleValues = (locale: string | undefined | null) => {
  // Normalize before lookup — locales arrive from store/partner config and
  // can differ in case or carry stray whitespace ("PT-BR", "en ").
  const key = typeof locale === "string" ? locale.trim().toLowerCase() : DEFAULT_LOCALE_KEY;
  return MAILBOX_SUGGESTION_VALUES[key] ?? MAILBOX_SUGGESTION_VALUES[DEFAULT_LOCALE_KEY];
};

export function getSuggestions({
  locale,
  existingUsernames = [],
  count = 5,
}: TGetSuggestionsOptions): TMailboxSuggestion[] {
  const values = resolveLocaleValues(locale);
  const taken = new Set(existingUsernames.map(u => String(u).toLowerCase()));
  const out: TMailboxSuggestion[] = [];
  for (let i = 0; i < MAILBOX_SUGGESTION_IDS.length && out.length < count; i++) {
    const username = values[MAILBOX_SUGGESTION_IDS[i]];
    if (!username) continue;
    if (taken.has(username.toLowerCase())) continue;
    // Popularity tracks the OUTPUT position, not the source slot — so when a
    // popular-slot username is already created and gets filtered out, the
    // next surviving suggestion takes its popular badge (the first two
    // suggestions are always tagged "popular").
    out.push({ username, popular: out.length < MAILBOX_SUGGESTION_POPULAR_COUNT });
  }
  return out;
}

export const __test__ = { resolveLocaleValues };
